import { useState, useEffect, useContext } from "react";
import { Flex, Typography, App, Spin, Pagination } from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import CustomSelect from "@/components/inputs/CustomSelect";
import SystemLogsTable from "@/components/tables/SystemLogsTable";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList as getLogs } from "@/api/systemLogs";
import { getEmployeesOptions } from "@/api/employees";

const LOCAL_STORAGE_KEY = "loggingTableParams";

const { Title } = Typography;

const CHANGE_TYPE_OPTIONS = [
  { label: "Создание", value: "created" },
  { label: "Изменение", value: "updated" },
  { label: "Удаление", value: "deleted" },
];

const ENTITY_TYPE_OPTIONS = [
  { label: "Задача", value: "task" },
  // { label: "Ресурс", value: "object" },
  { label: "Пользователь", value: "user" },
  { label: "Сотрудник", value: "employee" },
  { label: "Файл", value: "file" },
  { label: "Папка", value: "folder" },
  { label: "Шаблон задачи", value: "task_template" },
  // { label: "Рабочая группа", value: "unit" },
  // { label: "Заявка", value: "request" },
];

const initialTableParams = {
  pagination: {
    current: 1,
    pageSize: 10,
    placement: ["bottomLeft"],
    showSizeChanger: true,
  },
  filters: {
    change_type: null,
    entity_type_code: null,
    changed_by: null,
  },
};

// Страница системных логов
const SystemLogging = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  // Данные таблицы
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Опции для фильтров
  const [optionsPending, setOptionsPending] = useState(true);
  const [authors, setAuthors] = useState([]);

  // Параметры таблицы (+ фильтры)
  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    setOptionsPending(true);
    try {
      const employeesResp = await getEmployeesOptions({ is_dismissed: false });

      // Нормализация списка сотрудников под селект "Автор"
      let employees = [];
      if (employeesResp?.employees) {
        const arr = employeesResp.employees;
        employees =
          arr.length && arr[0]?.employee ? arr.map((x) => x.employee) : arr;
      }

      const authorOpts = (employees || [])
        .filter((e) => e?.uuid)
        .map((e) => ({
          label:
            [e.lastname, e.firstname, e.patronymic].filter(Boolean).join(" ") ||
            e.email ||
            e.uuid,
          value: e.uuid,
        }));

      setAuthors(authorOpts);

      // Инициализация параметров из localStorage
      const saved =
        JSON.parse(
          localStorage.getItem(`${user?.user_uuid}_${LOCAL_STORAGE_KEY}`)
        ) || initialTableParams;

      setTableParams(saved);
      await fetchData(saved);
    } catch (error) {
      message.open({
        type: "error",
        content: error?.message || "Ошибка загрузки опций",
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  const setParams = (params) => {
    setTableParams(params);
    localStorage.setItem(
      `${user?.user_uuid}_${LOCAL_STORAGE_KEY}`,
      JSON.stringify(params)
    );
  };

  // Запрос на сервер для получения данных
  const fetchData = async (tParams) => {
    setIsLoading(true);

    const current = tParams || tableParams;
    const params = {
      page: current.pagination.current,
      perpage: current.pagination.pageSize,
      // фильтры
      change_type: current.filters.change_type,
      entity_type_code: current.filters.entity_type_code,
      changed_by: current.filters.changed_by,
    };

    try {
      const { logs, total, page, perPage } = await getLogs(params, true);
      setData(logs || []);

      const updated = {
        ...current,
        pagination: {
          ...current.pagination,
          total: total ?? 0,
          current: page ?? current.pagination.current,
          pageSize: perPage ?? current.pagination.pageSize,
        },
      };
      setParams(updated);
    } catch (error) {
      message.open({
        type: "error",
        content: error?.message || "Не удалось получить логи",
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updated = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updated);
    fetchData(updated);
  };

  // Обработчик смены фильтра
  const handleFilterChange = (name, val) => {
    updateFilters(
      tableParams,
      {
        [name]: val ?? null,
      },
      true
    );
  };

  // Пагинация/сортировка таблицы
  const handleTableChange = (pagination, _filters) => {
    const updated = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
    };

    setParams(updated);
    fetchData(updated);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const updatedTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: shouldResetPage ? 1 : current,
        pageSize,
      },
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <Flex vertical gap={25} className="h-100">
          {/* Хлебные крошки и заголовок */}
          <Flex vertical gap={10}>
            <Breadcrumbs
              items={[
                { title: "Административная панель" },
                { title: "Системные логи" },
              ]}
            />
            <Flex gap={20} align="center" justify="space-between">
              <Title level={2} className="no-margin">
                Системные логи
              </Title>
            </Flex>
          </Flex>

          <Flex gap={8} align="center" wrap="wrap">
            <CustomSelect
              placeholder="Тип изменения"
              allowClear
              options={CHANGE_TYPE_OPTIONS}
              style={{ width: 170 }}
              value={tableParams.filters.change_type}
              onChange={(v) => handleFilterChange("change_type", v)}
              popupMatchSelectWidth={false}
            />

            <CustomSelect
              placeholder="Тип сущности"
              allowClear
              options={ENTITY_TYPE_OPTIONS}
              style={{ width: 190 }}
              value={tableParams.filters.entity_type_code}
              onChange={(v) => handleFilterChange("entity_type_code", v)}
              popupMatchSelectWidth={false}
            />

            <CustomSelect
              placeholder="Автор"
              allowClear
              showSearch
              options={authors}
              optionFilterProp="label"
              style={{ width: 220 }}
              value={tableParams.filters.changed_by}
              onChange={(v) => handleFilterChange("changed_by", v)}
              popupMatchSelectWidth={false}
            />
          </Flex>

          <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
            <SystemLogsTable
              data={data}
              loading={isLoading}
              pagination={false}
              onChange={handleTableChange}
              scroll={{ x: "100%", y: tableHeight - headerHeight }}
            />
          </div>

          <div style={{ minHeight: 32 }}>
            <Pagination
              {...tableParams.pagination}
              onChange={handlePaginationChange}
            />
          </div>
        </Flex>
      )}
    </>
  );
};

export default SystemLogging;
